import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.#controller.signal.aborted) {
      this.#controller = new AbortController();
      this.#handleRecentlyViewedChange();
    }

    const { dialog } = this;
    const { signal } = this.#controller;
    document.addEventListener(RecentlyViewed.eventName, this.#handleRecentlyViewedChange, { signal });

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (this.matches('predictive-search-real')) {
      const loadEmptyStateOnIntent = () => this.#loadEmptyState();
      const openInlineSearch = () => {
        this.classList.remove('predictive-search--closed');
      };

      this.refs.searchInput.addEventListener('pointerenter', loadEmptyStateOnIntent, {
        once: true,
        passive: true,
        signal,
      });
      this.refs.searchInput.addEventListener('focus', loadEmptyStateOnIntent, { once: true, signal });
      this.refs.searchInput.addEventListener('focus', openInlineSearch, { signal });
      this.refs.searchInput.addEventListener('pointerdown', openInlineSearch, { signal });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#activeFetch?.abort();
    this.#emptyStateLoaded = false;
  }

  // REAL: Remove history immediately across both search surfaces, including on withdrawal.
  #handleRecentlyViewedChange = () => {
    for (const wrapper of this.querySelectorAll('[ref="recentlyViewedWrapper"]')) {
      if (wrapper.contains(document.activeElement)) this.refs.searchInput.focus();
      wrapper.remove();
    }
    if (this.#emptyStateLoaded && !this.refs.searchInput.value.trim()) this.#resetSearch(false, true);
  };

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch(true, true);
  };

  #handleDialogOpen = () => {
    this.#loadEmptyState();
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false, !this.matches('predictive-search-real'));
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  // REAL: Rich suggestions remain native links in a labelled results region.
  get #resultLinks() {
    return [...new Set(this.#allResultsItems.map((item) => item.querySelector('a[href]')).filter(Boolean))];
  }

  /** @param {KeyboardEvent} event */
  onSearchKeyDown = (event) => {
    const originalTarget = event.composedPath?.()[0] ?? event.target;
    const { searchInput } = this.refs;

    if (event.key === 'Escape') {
      if (this.matches('predictive-search-real')) {
        event.preventDefault();
        searchInput.focus();
        this.classList.add('predictive-search--closed');
        this.#resetSearch(true, true);
      }
      // The modal owns Escape and restores its trigger before resetting results.
      return;
    }

    // REAL: Text editing and native controls do not need to traverse the result cards.
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Enter') return;

    const links = this.#resultLinks;
    const currentIndex = links.indexOf(originalTarget);
    const isInput = originalTarget === searchInput;
    if (!isInput && currentIndex === -1) return;

    if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && links.length) {
      event.preventDefault();
      const nextIndex = isInput
        ? (event.key === 'ArrowDown' ? 0 : links.length - 1)
        : currentIndex + (event.key === 'ArrowDown' ? 1 : -1);
      const next = links[nextIndex] ?? searchInput;
      next.focus({ preventScroll: true });
      // Reveal focus immediately, including during rapid key repeat and reduced motion.
      next.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'nearest' });
      return;
    }

    // Buttons, links, Tab, and text-editing keys keep their native behavior.
    if (!isInput || event.key !== 'Enter' || !links.length) return;
    const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
    if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
      event.preventDefault();
      window.location.href = singleResultContainer.dataset.singleResultUrl;
      return;
    }

    if (this.matches('predictive-search-real')) {
      event.preventDefault();
      event.stopPropagation();
      const form = this.querySelector('form');
      if (form instanceof HTMLFormElement) {
        const searchUrl = new URL(form.action, location.origin);
        const searchParams = new URLSearchParams();
        for (const [key, value] of new FormData(form)) {
          if (typeof value === 'string') searchParams.append(key, value);
        }
        searchUrl.search = searchParams.toString();
        window.location.href = searchUrl.toString();
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   * @param {boolean} [clearInput=true] - Whether to clear the current query
   */
  resetSearch = debounce((keepFocus = true, clearInput = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch(clearInput);
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Refresh suggestions or restore the empty state when the query is cleared.
   */
  search = debounce(() => {
    if (this.matches('predictive-search-real')) {
      this.classList.remove('predictive-search--closed');
    }

    const searchTerm = this.refs.searchInput.value.trim();

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /** Keep each search input connected to its own region after result replacement. */
  #syncResultsId() {
    const popupId = this.refs.searchInput.getAttribute('aria-controls');
    const popup = this.refs.predictiveSearchResults.querySelector('[role="region"]');
    if (popupId && popup) popup.id = popupId;
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,variants.sku');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        const focusedResults = predictiveSearchResults.contains(document.activeElement);
        if (focusedResults) this.refs.searchInput.focus();
        const resultsId = predictiveSearchResults.id;
        morph(predictiveSearchResults, resultsMarkup);
        if (resultsId) predictiveSearchResults.id = resultsId;
        this.#syncResultsId();

        this.#resetScrollPositions();

        // Count all result items (products, collections, pages, articles, queries)
        const resultCount = predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const limitedViewedProducts = viewedProducts.slice(0, 4);
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set(
      'q',
      limitedViewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR ')
    );
    url.searchParams.set('resources[type]', 'product');

    const revision = RecentlyViewed.revision;
    const markup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
    return RecentlyViewed.isAllowed() && revision === RecentlyViewed.revision ? markup : null;
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async (clearInput = true, preserveFocus = false) => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    if (!preserveFocus && predictiveSearchResults.contains(document.activeElement)) searchInput.focus();
    if (clearInput) {
      searchInput.value = '';
      this.#hideResetButton();
    }

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    if (abortController.signal.aborted) return;
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();
    const historyRevision = RecentlyViewed.revision;

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (recentlyViewedMarkup) {
        const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
        const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
        const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
        if (recentlyViewedProductsHtml && collectionElement) {
          for (const child of recentlyViewedProductsHtml.children) {
            if (child instanceof HTMLElement) child.setAttribute('ref', 'recentlyViewedWrapper');
          }
          collectionElement.prepend(...recentlyViewedProductsHtml.children);
        }
      }
    }

    if (abortController.signal.aborted) return;
    if (!RecentlyViewed.isAllowed() || historyRevision !== RecentlyViewed.revision) {
      parsedEmptySectionMarkup.querySelectorAll('[ref="recentlyViewedWrapper"]').forEach((wrapper) => wrapper.remove());
    }

    const resultsId = predictiveSearchResults.id;
    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    if (resultsId) predictiveSearchResults.id = resultsId;
    this.#syncResultsId();
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', class extends PredictiveSearchComponent {});
}

if (!customElements.get('predictive-search-real')) {
  customElements.define('predictive-search-real', class extends PredictiveSearchComponent {});
}
